// ABOUTME: The page a custom page detail was opened from, carried in the router location state.
// ABOUTME: Written when a widget opens a node in another page, read by the detail widgets' back button.

/** Where the back button of the opened detail returns, and the page name it shows. */
export interface NavigationOrigin {
    path: string;
    label: string;
    /**
     * The router state of the origin page itself, restored when going back to it: a detail that
     * was in turn opened from another page keeps its own origin, so the back chain spans every hop.
     */
    state?: Record<string, unknown>;
}

const ORIGIN_STATE_KEY = 'customPageOrigin';

/** Router state for a navigation that opens a detail from `origin`. */
export function buildNavigationOriginState(origin: NavigationOrigin): Record<string, unknown> {
    return { [ORIGIN_STATE_KEY]: origin };
}

/**
 * Reads the origin from a router location state. React Router keeps the state in `history.state`,
 * so it survives a reload of the detail; a pasted link or a bookmark carries none.
 */
export function readNavigationOrigin(state: unknown): NavigationOrigin | undefined {
    if (!state || typeof state !== 'object') return undefined;
    const raw = (state as Record<string, unknown>)[ORIGIN_STATE_KEY];
    if (!raw || typeof raw !== 'object') return undefined;
    const {
        path,
        label,
        state: originState,
    } = raw as {
        path?: unknown;
        label?: unknown;
        state?: unknown;
    };
    if (typeof path !== 'string' || !path.startsWith('/')) return undefined;
    return {
        path,
        label: typeof label === 'string' ? label : '',
        ...(originState && typeof originState === 'object'
            ? { state: originState as Record<string, unknown> }
            : {}),
    };
}
